import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Mail, Users, ShieldCheck, Film, FileDown } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [media, setMedia] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [modules, setModules] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessionForm, setSessionForm] = useState({ title: "", date: "", duration: "60 min", description: "", zoom_url: "" });
  const [retreatRegs, setRetreatRegs] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/courses", { params: { level: 1 } }).then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/admin/media").then(({ data }) => setMedia(data)).catch(() => {});
    api.get("/live-sessions").then(({ data }) => setSessions(data)).catch(() => {});
    api.get("/admin/retreat-registrations").then(({ data }) => setRetreatRegs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/auth");
    if (user?.role === "admin") loadAll();
  }, [user, loading, navigate, loadAll]);

  useEffect(() => {
    if (!courseId) { setModules([]); return; }
    api.get(`/courses/${courseId}`).then(({ data }) => setModules(data.modules)).catch(() => {});
  }, [courseId]);

  if (loading || !user || user.role !== "admin") return null;

  const upload = async () => {
    if (!courseId || !moduleId || !file) { toast.error("Choisissez cours, module et fichier"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("course_id", courseId);
      fd.append("module_id", moduleId);
      fd.append("file", file);
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Média téléversé");
      setFile(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setBusy(false);
    }
  };

  const addSession = async () => {
    if (!sessionForm.title || !sessionForm.date || !sessionForm.zoom_url) { toast.error("Titre, date et lien Zoom requis"); return; }
    try {
      await api.post("/admin/live-sessions", { ...sessionForm, date: new Date(sessionForm.date).toISOString() });
      toast.success("Session créée");
      setSessionForm({ title: "", date: "", duration: "60 min", description: "", zoom_url: "" });
      loadAll();
    } catch { toast.error("Erreur"); }
  };

  const sendTest = async () => {
    const { data } = await api.post("/admin/test-email");
    if (data.sent) toast.success("Email de test envoyé !");
    else toast.warning(data.configured ? "Échec d'envoi — vérifiez les identifiants" : "Gmail non configuré (GMAIL_ADDRESS / GMAIL_APP_PASSWORD)");
  };

  const input = "w-full bg-input border border-primary/20 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none";

  return (
    <main className="max-w-6xl mx-auto px-6 py-16 relative z-10" data-testid="admin-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary flex items-center gap-2"><ShieldCheck size={14} /> Back-office</p>
      <h1 className="text-4xl font-serif-display mt-2">Administration</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mt-10" data-testid="admin-stats">
          {[["Utilisateurs", stats.users], ["Achats", stats.purchases], ["Certificats", stats.certificates], ["Médias", stats.media], ["Inscriptions live", stats.registrations], ["Attente Niv. 2", stats.waitlist_l2], ["Attente Niv. 3", stats.waitlist_l3], ["Retraites", stats.retreat_registrations]].map(([label, val]) => (
            <div key={label} className="bg-card border border-primary/15 rounded-lg p-5">
              <p className="font-serif-display text-3xl text-primary">{val}</p>
              <p className="text-xs text-muted-foreground mt-1">{label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        {/* Upload */}
        <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="admin-upload-section">
          <h2 className="font-serif-display text-2xl flex items-center gap-3"><Upload size={18} className="text-primary" /> Téléverser un média</h2>
          <p className="text-xs text-muted-foreground mt-2">Vidéo (mp4, webm, mov), audio (mp3, wav, m4a, ogg) ou PDF — max 200 Mo. Le média remplace le placeholder du module.</p>
          <div className="space-y-3 mt-5">
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={input} data-testid="admin-course-select">
              <option value="">— Cours —</option>
              {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
            </select>
            <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} className={input} data-testid="admin-module-select">
              <option value="">— Module —</option>
              {modules.map((m) => <option key={m.module_id} value={m.module_id}>{m.title} ({m.type})</option>)}
            </select>
            <input type="file" accept=".mp4,.webm,.mov,.mp3,.wav,.m4a,.ogg,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground file:text-xs" data-testid="admin-file-input" />
            <button onClick={upload} disabled={busy}
              className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200 disabled:opacity-50" data-testid="admin-upload-btn">
              {busy ? "Envoi…" : "Téléverser"}
            </button>
          </div>
          <div className="mt-6 space-y-2">
            {media.map((m) => (
              <div key={m.media_id} className="flex items-center justify-between text-sm border border-primary/10 rounded-lg px-4 py-2.5" data-testid={`media-item-${m.media_id}`}>
                <span className="flex items-center gap-2 truncate"><Film size={14} className="text-primary shrink-0" /> {m.course_id}/{m.module_id} — {m.original_filename}</span>
                <button onClick={async () => { await api.delete(`/admin/media/${m.media_id}`); loadAll(); }}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`media-delete-${m.media_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Live sessions */}
        <section className="bg-card border border-primary/15 rounded-lg p-7" data-testid="admin-sessions-section">
          <h2 className="font-serif-display text-2xl flex items-center gap-3"><Users size={18} className="text-primary" /> Sessions live</h2>
          <div className="space-y-3 mt-5">
            <input placeholder="Titre" value={sessionForm.title} onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })} className={input} data-testid="session-title-input" />
            <div className="flex gap-3">
              <input type="datetime-local" value={sessionForm.date} onChange={(e) => setSessionForm({ ...sessionForm, date: e.target.value })} className={input} data-testid="session-date-input" />
              <input placeholder="Durée" value={sessionForm.duration} onChange={(e) => setSessionForm({ ...sessionForm, duration: e.target.value })} className={input} />
            </div>
            <input placeholder="Lien Zoom" value={sessionForm.zoom_url} onChange={(e) => setSessionForm({ ...sessionForm, zoom_url: e.target.value })} className={input} data-testid="session-zoom-input" />
            <textarea placeholder="Description" rows={2} value={sessionForm.description} onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })} className={input} />
            <button onClick={addSession} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="session-add-btn">
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <div className="mt-6 space-y-2">
            {sessions.map((s) => (
              <div key={s.session_id} className="flex items-center justify-between text-sm border border-primary/10 rounded-lg px-4 py-2.5" data-testid={`admin-session-${s.session_id}`}>
                <span className="truncate">{s.title} — {new Date(s.date).toLocaleDateString("fr-CA")}</span>
                <button onClick={async () => { await api.delete(`/admin/live-sessions/${s.session_id}`); loadAll(); }}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`session-delete-${s.session_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-primary/10">
            <p className="text-xs text-muted-foreground mb-3">
              Rappels automatiques : 24 h et 1 h avant chaque session, aux inscrits.
              {stats && !stats.email_configured && <span className="text-destructive"> Gmail non configuré — les envois sont simulés.</span>}
            </p>
            <button onClick={sendTest} className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200" data-testid="test-email-btn">
              <Mail size={13} /> Envoyer un email de test
            </button>
          </div>
        </section>
      </div>

      {/* Pré-inscriptions retraites */}
      <section className="bg-card border border-primary/15 rounded-lg p-7 mt-8" data-testid="admin-retreats-section">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-serif-display text-2xl">Pré-inscriptions aux retraites</h2>
          <div className="flex flex-wrap gap-3">
            <a href={`${API}/admin/export/retreats.csv`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200"
              data-testid="export-retreats-btn">
              <FileDown size={13} /> Export CSV retraites
            </a>
            <a href={`${API}/admin/export/waitlist.csv`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200"
              data-testid="export-waitlist-btn">
              <FileDown size={13} /> Export CSV listes d'attente
            </a>
            <a href={`${API}/admin/export/newsletter.csv`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200"
              data-testid="export-newsletter-btn">
              <FileDown size={13} /> Export CSV infolettre
            </a>
          </div>
        </div>
        {retreatRegs.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-4">Aucune pré-inscription pour le moment.</p>
        ) : (
          <div className="mt-5 space-y-2">
            {retreatRegs.map((r) => (
              <div key={r.registration_id} className="text-sm border border-primary/10 rounded-lg px-4 py-3 flex flex-wrap gap-x-6 gap-y-1" data-testid={`retreat-reg-${r.registration_id}`}>
                <span className="font-medium">{r.name}</span>
                <span className="text-muted-foreground">{r.email}{r.phone ? ` · ${r.phone}` : ""}</span>
                <span className="text-primary">{r.destination}</span>
                {r.period && <span className="text-muted-foreground">{r.period}</span>}
                <span className="text-xs text-muted-foreground ml-auto">{r.created_at?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
